/** Flags de sesión en sessionStorage para coordinar login biométrico y recargas del SW. */

export const SHC_BIOMETRIC_IN_PROGRESS = 'shc:biometric-in-progress';
export const SHC_LOGIN_UI_COMPLETE = 'shc:login-ui-complete';
export const SHC_RECOVER_SESSION_AFTER_RELOAD = 'shc:recover-session-after-reload';
/** Atender SoftPhone/timbre saltando huella (JWT vivo en localStorage). */
export const SHC_SOFTPHONE_QUICK_ANSWER = 'shc:softphone-quick-answer';
/** Sesión de llamada de timbre (UI en Teléfono, no en Video). */
export const SHC_SOFTPHONE_DOORBELL_SESSION = 'shc:softphone-doorbell-session';
/** Tras colgar quick-answer: no restaurar Video/timbre desde bfcache / recents. */
export const SHC_SOFTPHONE_CALL_ENDED_GATE = 'shc:softphone-call-ended-gate';

export function setBiometricInProgress(active: boolean): void {
  if (active) {
    sessionStorage.setItem(SHC_BIOMETRIC_IN_PROGRESS, '1');
  } else {
    sessionStorage.removeItem(SHC_BIOMETRIC_IN_PROGRESS);
  }
}

export function isBiometricInProgress(): boolean {
  return sessionStorage.getItem(SHC_BIOMETRIC_IN_PROGRESS) === '1';
}

export function setLoginUiComplete(complete: boolean): void {
  if (complete) {
    sessionStorage.setItem(SHC_LOGIN_UI_COMPLETE, '1');
  } else {
    sessionStorage.removeItem(SHC_LOGIN_UI_COMPLETE);
  }
}

export function isLoginUiComplete(): boolean {
  return sessionStorage.getItem(SHC_LOGIN_UI_COMPLETE) === '1';
}

/** Solo recargar por actualización del SW si la app ya terminó el login y no hay huella en curso. */
export function canSafelyReloadForSwUpdate(): boolean {
  return isLoginUiComplete() && !isBiometricInProgress();
}

export function markRecoverSessionAfterReload(): void {
  sessionStorage.setItem(SHC_RECOVER_SESSION_AFTER_RELOAD, '1');
}

export function shouldRecoverSessionAfterReload(): boolean {
  return sessionStorage.getItem(SHC_RECOVER_SESSION_AFTER_RELOAD) === '1';
}

export function clearRecoverSessionAfterReload(): void {
  sessionStorage.removeItem(SHC_RECOVER_SESSION_AFTER_RELOAD);
}

export function clearLoginSessionFlags(): void {
  sessionStorage.removeItem(SHC_BIOMETRIC_IN_PROGRESS);
  sessionStorage.removeItem(SHC_LOGIN_UI_COMPLETE);
  sessionStorage.removeItem(SHC_RECOVER_SESSION_AFTER_RELOAD);
  sessionStorage.removeItem(SHC_SOFTPHONE_QUICK_ANSWER);
  sessionStorage.removeItem(SHC_SOFTPHONE_DOORBELL_SESSION);
  sessionStorage.removeItem(SHC_PENDING_DOORBELL);
  sessionStorage.removeItem(SHC_SOFTPHONE_CALL_ENDED_GATE);
  sessionStorage.removeItem(SHC_SOFTPHONE_INCOMING_RESUME);
  sessionStorage.removeItem('shc:softphone-incoming-handled');
  clearSoftphoneBgIncomingLocal();
  void clearIncomingResumeFromCache();
}

/**
 * La PWA ya había pasado el login en ESTE documento (sessionStorage).
 * Un remount de Android (start_url / openWindow) conserva sessionStorage;
 * un arranque en frío (app cerrada) no. Sirve para no pedir huella ni
 * marcar quick-answer cuando la app estaba abierta en segundo plano.
 */
export function hasLoggedInDocumentSession(): boolean {
  return isLoginUiComplete();
}

/** Ring recibido con la PWA viva: si Android remonta el documento, reanudar sin huella. */
export const SHC_SOFTPHONE_INCOMING_RESUME = 'shc:softphone-incoming-resume';
const INCOMING_RESUME_MAX_AGE_MS = 2 * 60 * 1000;

/** Esta llamada ya llegó (INVITE) o se colgó: no mostrar “esperando registro”. */
export const SHC_SOFTPHONE_INCOMING_HANDLED = 'shc:softphone-incoming-handled';

export function markIncomingSoftphoneHandled(): void {
  try {
    sessionStorage.setItem(SHC_SOFTPHONE_INCOMING_HANDLED, '1');
  } catch {
    // ignorar
  }
}

export function isIncomingSoftphoneHandled(): boolean {
  try {
    return sessionStorage.getItem(SHC_SOFTPHONE_INCOMING_HANDLED) === '1';
  } catch {
    return false;
  }
}

export function clearIncomingSoftphoneHandled(): void {
  try {
    sessionStorage.removeItem(SHC_SOFTPHONE_INCOMING_HANDLED);
  } catch {
    // ignorar
  }
}

export function markSoftphoneIncomingResume(): void {
  try {
    sessionStorage.setItem(SHC_SOFTPHONE_INCOMING_RESUME, String(Date.now()));
    sessionStorage.removeItem(SHC_SOFTPHONE_INCOMING_HANDLED);
  } catch {
    // ignorar
  }
}

export function isSoftphoneIncomingResume(maxAgeMs: number = INCOMING_RESUME_MAX_AGE_MS): boolean {
  try {
    const raw = sessionStorage.getItem(SHC_SOFTPHONE_INCOMING_RESUME);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) {
      sessionStorage.removeItem(SHC_SOFTPHONE_INCOMING_RESUME);
      return false;
    }
    if (Date.now() - ts > maxAgeMs) {
      sessionStorage.removeItem(SHC_SOFTPHONE_INCOMING_RESUME);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function clearSoftphoneIncomingResume(): void {
  try {
    sessionStorage.removeItem(SHC_SOFTPHONE_INCOMING_RESUME);
  } catch {
    // ignorar
  }
}

/**
 * localStorage: la instancia en background lo escribe al recibir el ring.
 * Un documento NUEVO (Android start_url) lo puede leer; sessionStorage no se comparte.
 */
export const SHC_SOFTPHONE_BG_INCOMING = 'shc:softphone-bg-incoming';

export function markSoftphoneBgIncomingLocal(): void {
  try {
    localStorage.setItem(SHC_SOFTPHONE_BG_INCOMING, String(Date.now()));
    sessionStorage.removeItem(SHC_SOFTPHONE_INCOMING_HANDLED);
  } catch {
    // ignorar
  }
}

export function isSoftphoneBgIncomingLocal(maxAgeMs: number = INCOMING_RESUME_MAX_AGE_MS): boolean {
  try {
    const raw = localStorage.getItem(SHC_SOFTPHONE_BG_INCOMING);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts) || Date.now() - ts > maxAgeMs) {
      localStorage.removeItem(SHC_SOFTPHONE_BG_INCOMING);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function clearSoftphoneBgIncomingLocal(): void {
  try {
    localStorage.removeItem(SHC_SOFTPHONE_BG_INCOMING);
  } catch {
    // ignorar
  }
}

/** Cache API: el SW lo escribe al push/click (la página congelada no corre JS). */
export const SHC_INCOMING_RESUME_CACHE = 'shc-session-flags';
export const SHC_INCOMING_RESUME_CACHE_URL = '/pwa/shc-incoming-resume';

export async function readIncomingResumeFromCache(
  maxAgeMs: number = INCOMING_RESUME_MAX_AGE_MS,
): Promise<boolean> {
  try {
    if (typeof caches === 'undefined') return false;
    const cache = await caches.open(SHC_INCOMING_RESUME_CACHE);
    const res = await cache.match(SHC_INCOMING_RESUME_CACHE_URL);
    if (!res) return false;
    const data = await res.json().catch(() => null);
    const ts = Number(data?.ts);
    if (!ts || Date.now() - ts > maxAgeMs) {
      await cache.delete(SHC_INCOMING_RESUME_CACHE_URL);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export async function clearIncomingResumeFromCache(): Promise<void> {
  try {
    if (typeof caches === 'undefined') return;
    const cache = await caches.open(SHC_INCOMING_RESUME_CACHE);
    await cache.delete(SHC_INCOMING_RESUME_CACHE_URL);
  } catch {
    // ignorar
  }
}

/** Deep link de videollamada/teléfono (answer=1 sin flags de timbre). */
export function isPhoneIncomingDeepLink(href?: string): boolean {
  try {
    if (isSoftphoneDoorbellDeepLink(href)) return false;
    const u = new URL(href || (typeof window !== 'undefined' ? window.location.href : ''), 'http://local');
    const screen = (u.searchParams.get('screen') || '').toLowerCase();
    return screen === 'softphone' && u.searchParams.get('answer') === '1';
  } catch {
    return false;
  }
}

export function clearDoorbellIncomingContext(): void {
  clearSoftphoneDoorbellSession();
  clearPendingDoorbellCamera();
}

/** Intent de atender TELÉFONO (no timbre): URL, sessionStorage o localStorage. */
export function hasIncomingSoftphoneIntent(): boolean {
  if (isPhoneIncomingDeepLink()) return true;
  if (isDoorbellIncomingContext()) return false;
  return isSoftphoneAnswerDeepLink()
    || isSoftphoneIncomingResume()
    || isSoftphoneBgIncomingLocal();
}

/**
 * Reclaim / AMI Redirect de teléfono: solo tap de notificación o remount
 * (`answer=1` / quick-answer). Un push con la PWA ya visible NO es handoff:
 * el INVITE llega al Contact WebRTC; Redirect lo cancela (PWA↔PWA).
 */
export function needsPhoneHandoff(): boolean {
  if (isDoorbellIncomingContext()) return false;
  if (isIncomingSoftphoneHandled()) return false;
  return isPhoneIncomingDeepLink() || isSoftphoneQuickAnswer();
}

/** Consumir el intent (una llamada). El próximo arranque debe ir a la home, no a Teléfono. */
export function consumeIncomingSoftphoneIntent(): void {
  clearSoftphoneIncomingResume();
  clearSoftphoneBgIncomingLocal();
  void clearIncomingResumeFromCache();
  markIncomingSoftphoneHandled();
}

/** Flag de corte por inactividad: se muestra en LoginPage, nunca después de un login nuevo. */
export const PREVIOUS_SESSION_TIMEOUT_KEY = 'previous_session_timeout';
const PREVIOUS_SESSION_TIMEOUT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function markPreviousSessionTimeout(): void {
  try {
    sessionStorage.setItem(PREVIOUS_SESSION_TIMEOUT_KEY, String(Date.now()));
  } catch {
    // sessionStorage puede no estar disponible
  }
}

export function hasRecentPreviousSessionTimeout(
  maxAgeMs: number = PREVIOUS_SESSION_TIMEOUT_MAX_AGE_MS
): boolean {
  try {
    const raw = sessionStorage.getItem(PREVIOUS_SESSION_TIMEOUT_KEY);
    if (!raw) return false;
    // Flag legado (boolean): tratarlo como vigente hasta el próximo login.
    if (raw === 'true') return true;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) {
      sessionStorage.removeItem(PREVIOUS_SESSION_TIMEOUT_KEY);
      return false;
    }
    if (Date.now() - ts > maxAgeMs) {
      sessionStorage.removeItem(PREVIOUS_SESSION_TIMEOUT_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function clearPreviousSessionTimeout(): void {
  try {
    sessionStorage.removeItem(PREVIOUS_SESSION_TIMEOUT_KEY);
  } catch {
    // ignorar
  }
}

/** Avisos de logout que deben verse en LoginPage (no como snackbar post-login). */
export const LOGIN_NOTICE_KEY = 'shc:login_notice';
export type LoginNoticeKind = 'tokenExpired' | 'userDisabled';

export function markLoginNotice(kind: LoginNoticeKind): void {
  try {
    sessionStorage.setItem(LOGIN_NOTICE_KEY, kind);
  } catch {
    // ignorar
  }
}

export function peekLoginNotice(): LoginNoticeKind | null {
  try {
    const raw = sessionStorage.getItem(LOGIN_NOTICE_KEY);
    if (raw === 'tokenExpired' || raw === 'userDisabled') return raw;
    return null;
  } catch {
    return null;
  }
}

export function clearLoginNotice(): void {
  try {
    sessionStorage.removeItem(LOGIN_NOTICE_KEY);
  } catch {
    // ignorar
  }
}

/** Atender SoftPhone/timbre saltando huella (JWT vivo en localStorage). */

export function markSoftphoneQuickAnswer(): void {
  try {
    sessionStorage.setItem(SHC_SOFTPHONE_QUICK_ANSWER, '1');
    sessionStorage.removeItem(SHC_SOFTPHONE_INCOMING_HANDLED);
  } catch {
    // ignorar
  }
}

export function isSoftphoneQuickAnswer(): boolean {
  try {
    return sessionStorage.getItem(SHC_SOFTPHONE_QUICK_ANSWER) === '1';
  } catch {
    return false;
  }
}

export function clearSoftphoneQuickAnswer(): void {
  try {
    sessionStorage.removeItem(SHC_SOFTPHONE_QUICK_ANSWER);
    sessionStorage.removeItem(SHC_SOFTPHONE_DOORBELL_SESSION);
  } catch {
    // ignorar
  }
}

export function markSoftphoneDoorbellSession(): void {
  try {
    sessionStorage.setItem(SHC_SOFTPHONE_DOORBELL_SESSION, '1');
  } catch {
    // ignorar
  }
}

export function isSoftphoneDoorbellSession(): boolean {
  try {
    return sessionStorage.getItem(SHC_SOFTPHONE_DOORBELL_SESSION) === '1';
  } catch {
    return false;
  }
}

export function clearSoftphoneDoorbellSession(): void {
  try {
    sessionStorage.removeItem(SHC_SOFTPHONE_DOORBELL_SESSION);
  } catch {
    // ignorar
  }
}

export function markSoftphoneCallEndedGate(): void {
  try {
    sessionStorage.setItem(SHC_SOFTPHONE_CALL_ENDED_GATE, '1');
  } catch {
    // ignorar
  }
}

export function isSoftphoneCallEndedGate(): boolean {
  try {
    return sessionStorage.getItem(SHC_SOFTPHONE_CALL_ENDED_GATE) === '1';
  } catch {
    return false;
  }
}

export function clearSoftphoneCallEndedGate(): void {
  try {
    sessionStorage.removeItem(SHC_SOFTPHONE_CALL_ENDED_GATE);
  } catch {
    // ignorar
  }
}

/** Deep link de atender llamada/timbre (query en la URL actual). */
export function isSoftphoneAnswerDeepLink(href?: string): boolean {
  try {
    const u = new URL(href || (typeof window !== 'undefined' ? window.location.href : ''), 'http://local');
    const screen = (u.searchParams.get('screen') || '').toLowerCase();
    if (screen === 'softphone' && u.searchParams.get('answer') === '1') return true;
    if (screen === 'video' && u.searchParams.get('softphone') === 'answer') return true;
    if (screen === 'softphone' && u.searchParams.get('kind') === 'doorbell') return true;
    return false;
  } catch {
    return false;
  }
}

/** Isla / sesión / URL de timbre: no hacer reclaim ni claim de teléfono. */
export function isDoorbellIncomingContext(href?: string): boolean {
  if (isPhoneIncomingDeepLink(href)) return false;
  return isSoftphoneDoorbellDeepLink(href)
    || isSoftphoneDoorbellSession()
    || peekPendingDoorbellCamera() != null
    || getDoorbellCameraFromUrl(href) != null;
}

/** Deep link de timbre (no llamada de voz). Incluye URL legacy screen=video. */
export function isSoftphoneDoorbellDeepLink(href?: string): boolean {
  try {
    const u = new URL(href || (typeof window !== 'undefined' ? window.location.href : ''), 'http://local');
    const screen = (u.searchParams.get('screen') || '').toLowerCase();
    if (u.searchParams.get('kind') === 'doorbell') return true;
    if (u.searchParams.get('softphone') === 'answer') return true;
    if (screen === 'video' && u.searchParams.get('softphone') === 'answer') return true;
    if (screen === 'softphone' && u.searchParams.get('camera')) return true;
    return false;
  } catch {
    return false;
  }
}

/** Timbre: abrir Video → fullscreen de UNA cámara (sin montar la grilla completa). */
export const SHC_PENDING_DOORBELL = 'shc:pending-doorbell';
const PENDING_DOORBELL_MAX_AGE_MS = 2 * 60 * 1000;

export type PendingDoorbell = { cameraId: number; cameraName?: string };

export function setPendingDoorbellCamera(cameraId: number, cameraName?: string): void {
  try {
    const id = Number(cameraId);
    if (!Number.isFinite(id)) return;
    sessionStorage.setItem(
      SHC_PENDING_DOORBELL,
      JSON.stringify({
        cameraId: id,
        cameraName: cameraName || null,
        ts: Date.now(),
      }),
    );
  } catch {
    // ignorar
  }
}

function parsePendingDoorbell(raw: string | null): PendingDoorbell | null {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    const id = Number(data?.cameraId);
    if (!Number.isFinite(id)) return null;
    const ts = Number(data?.ts) || 0;
    if (ts && Date.now() - ts > PENDING_DOORBELL_MAX_AGE_MS) return null;
    const name = typeof data?.cameraName === 'string' && data.cameraName ? data.cameraName : undefined;
    return { cameraId: id, cameraName: name };
  } catch {
    return null;
  }
}

export function peekPendingDoorbellCamera(): PendingDoorbell | null {
  try {
    return parsePendingDoorbell(sessionStorage.getItem(SHC_PENDING_DOORBELL));
  } catch {
    return null;
  }
}

export function consumePendingDoorbellCamera(): PendingDoorbell | null {
  try {
    const parsed = parsePendingDoorbell(sessionStorage.getItem(SHC_PENDING_DOORBELL));
    sessionStorage.removeItem(SHC_PENDING_DOORBELL);
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingDoorbellCamera(): void {
  try {
    sessionStorage.removeItem(SHC_PENDING_DOORBELL);
  } catch {
    // ignorar
  }
}

/** Lee cámara de timbre desde URL (legacy video o Teléfono). */
export function getDoorbellCameraFromUrl(href?: string): number | null {
  try {
    const u = new URL(href || (typeof window !== 'undefined' ? window.location.href : ''), 'http://local');
    const screen = (u.searchParams.get('screen') || '').toLowerCase();
    const isDoor =
      u.searchParams.get('softphone') === 'answer'
      || u.searchParams.get('kind') === 'doorbell'
      || (screen === 'softphone' && Boolean(u.searchParams.get('camera')));
    if (!isDoor) return null;
    const cam = parseInt(u.searchParams.get('camera') || '', 10);
    return Number.isFinite(cam) ? cam : 23;
  } catch {
    return null;
  }
}

/** JWT no expirado (solo lee `exp`; no valida firma — el API la valida). */
export function isStoredJwtAlive(token: string | null | undefined, skewSec = 30): boolean {
  if (!token || typeof token !== 'string') return false;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return false;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(atob(b64 + pad));
    const exp = Number(json?.exp);
    if (!exp) return true; // sin exp: dejar que el API decida
    return exp * 1000 > Date.now() + skewSec * 1000;
  } catch {
    return false;
  }
}
